import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Wrench, Package, User, Minus, Loader2, CheckCircle, Cog, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StockOut = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("spares"); // "spares" or "consumption"
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Spares state
  const [spareOptions, setSpareOptions] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedSpare, setSelectedSpare] = useState("");
  const [quantityOut, setQuantityOut] = useState("");
  const [personName, setPersonName] = useState("");

  // Consumption state
  const [consumptionOptions, setConsumptionOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [consumptionQty, setConsumptionQty] = useState("");
  const [consumptionPerson, setConsumptionPerson] = useState("");
  const [purpose, setPurpose] = useState("");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sparesRes, consumptionRes] = await Promise.all([
        axios.get(`${API}/spare-stock-out/options`).catch(() => ({ data: [] })),
        axios.get(`${API}/consumption-stock-out/options`).catch(() => ({ data: [] })),
      ]);
      setSpareOptions(sparesRes.data);
      setConsumptionOptions(consumptionRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Spares helpers
  const selectedMachineData = spareOptions.find((m) => m.machine_id === selectedMachine);
  const selectedSpareData = selectedMachineData?.spares?.find((s) => s.id === selectedSpare);
  const spareAvailableQty = selectedSpareData?.available || 0;

  // Consumption helpers
  const selectedCategoryData = consumptionOptions.find(c => c.category === selectedCategory);
  const selectedItemData = selectedCategoryData?.items?.find(i => i.id === selectedItem);
  const consumptionAvailableCount = selectedItemData?.available_count || 0;
  const consumptionUnit = selectedItemData?.unit || "units";

  const handleSpareSubmit = async () => {
    if (!selectedMachine) { toast.error("Select a machine"); return; }
    if (!selectedSpare) { toast.error("Select a spare"); return; }
    if (!quantityOut || parseInt(quantityOut) <= 0) { toast.error("Enter quantity"); return; }
    if (!personName.trim()) { toast.error("Enter your name"); return; }
    if (parseInt(quantityOut) > spareAvailableQty) { toast.error(`Only ${spareAvailableQty} available`); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/spare-stock-out`, {
        machine_id: selectedMachine,
        spare_uuid: selectedSpare,
        quantity_out: parseInt(quantityOut),
        person_name: personName.trim(),
      });
      setSuccess(true);
      toast.success("Stock out recorded!");
      setTimeout(() => {
        setSuccess(false);
        setSelectedMachine(""); setSelectedSpare(""); setQuantityOut(""); setPersonName("");
        fetchAll();
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record stock out");
    } finally { setSubmitting(false); }
  };

  const handleConsumptionSubmit = async () => {
    if (!selectedItem) { toast.error("Select an item"); return; }
    if (!consumptionQty || parseInt(consumptionQty) <= 0) { toast.error("Enter quantity"); return; }
    if (!consumptionPerson.trim()) { toast.error("Enter your name"); return; }
    if (parseInt(consumptionQty) > consumptionAvailableCount) { toast.error(`Only ${consumptionAvailableCount} nos available`); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/consumption-stock-out`, {
        item_id: selectedItem,
        count_out: parseInt(consumptionQty),
        person_name: consumptionPerson.trim(),
        purpose: purpose.trim(),
      });
      setSuccess(true);
      toast.success("Stock out recorded!");
      setTimeout(() => {
        setSuccess(false);
        setSelectedCategory(""); setSelectedItem(""); setConsumptionQty(""); setPurpose("");
        fetchAll();
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record stock out");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-950">
        <Loader2 className="h-10 w-10 animate-spin text-orange-400" />
      </div>
    );
  }

  const accentColor = mode === "spares" ? "orange" : "emerald";

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* Header */}
      <div className={`bg-gray-900 border-b ${mode === "spares" ? "border-orange-500/30" : "border-emerald-500/30"} px-4 py-3 flex items-center justify-between shrink-0`}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-gray-400 hover:text-white p-1" data-testid="stockout-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            {mode === "spares" ? <Wrench className="h-5 w-5 text-orange-400" /> : <Droplets className="h-5 w-5 text-emerald-400" />}
            <h1 className="text-lg font-bold text-white">Stock Out</h1>
          </div>
        </div>
        <Cog className="h-5 w-5 text-gray-600" />
      </div>

      {/* Mode Toggle */}
      <div className="px-4 pt-3 max-w-lg mx-auto w-full">
        <div className="flex bg-gray-900 border border-gray-800 rounded-lg p-1 gap-1">
          <button
            onClick={() => setMode("spares")}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              mode === "spares" ? "bg-orange-500 text-black" : "text-gray-400 hover:text-white"
            }`}
            data-testid="stockout-mode-spares"
          >
            <Wrench className="h-4 w-4" /> Spare Parts
          </button>
          <button
            onClick={() => setMode("consumption")}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              mode === "consumption" ? "bg-emerald-500 text-black" : "text-gray-400 hover:text-white"
            }`}
            data-testid="stockout-mode-consumption"
          >
            <Droplets className="h-4 w-4" /> Consumption
          </button>
        </div>
      </div>

      {/* Success overlay */}
      {success && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-950/90">
          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-3 animate-bounce" />
            <p className="text-green-400 text-xl font-bold">Recorded!</p>
          </div>
        </div>
      )}

      {/* SPARES FORM */}
      {mode === "spares" && (
        <div className="flex-1 px-4 py-3 flex flex-col gap-3 max-w-lg mx-auto w-full">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-3">
              <Label className="text-orange-400 text-xs font-bold uppercase tracking-wider">Machine</Label>
              <select value={selectedMachine} onChange={(e) => { setSelectedMachine(e.target.value); setSelectedSpare(""); }} className="w-full h-11 mt-1 px-3 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm" data-testid="stockout-machine-select">
                <option value="">Select Machine</option>
                {spareOptions.map((m) => (
                  <option key={m.machine_id} value={m.machine_id}>{m.machine_id} - {m.machine_name}</option>
                ))}
              </select>
            </CardContent>
          </Card>

          <Card className={`border ${selectedMachine ? 'bg-gray-900 border-gray-800' : 'bg-gray-900/50 border-gray-800/50 opacity-60'}`}>
            <CardContent className="p-3">
              <Label className="text-orange-400 text-xs font-bold uppercase tracking-wider">Spare</Label>
              <select value={selectedSpare} onChange={(e) => setSelectedSpare(e.target.value)} className="w-full h-11 mt-1 px-3 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm" disabled={!selectedMachine} data-testid="stockout-spare-select">
                <option value="">Select Spare</option>
                {selectedMachineData?.spares?.map((s) => (
                  <option key={s.id} value={s.id}>{s.spare_name} ({s.spare_id}) — Avail: {s.available}</option>
                ))}
              </select>
              {selectedSpareData && (
                <div className={`mt-2 px-3 py-1.5 rounded-lg text-sm font-mono font-bold ${spareAvailableQty > 0 ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                  <Package className="h-3.5 w-3.5 inline mr-1" /> Available: {spareAvailableQty}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={`border ${selectedSpare ? 'bg-gray-900 border-gray-800' : 'bg-gray-900/50 border-gray-800/50 opacity-60'}`}>
            <CardContent className="p-3">
              <Label className="text-orange-400 text-xs font-bold uppercase tracking-wider">Quantity Taking</Label>
              <Input type="number" min="1" max={spareAvailableQty} value={quantityOut} onChange={(e) => setQuantityOut(e.target.value)} placeholder="How many?" className="bg-gray-800 border-gray-700 text-white mt-1 h-11 text-lg" disabled={!selectedSpare} data-testid="stockout-qty-input" />
            </CardContent>
          </Card>

          <Card className={`border ${selectedSpare ? 'bg-gray-900 border-gray-800' : 'bg-gray-900/50 border-gray-800/50 opacity-60'}`}>
            <CardContent className="p-3">
              <Label className="text-orange-400 text-xs font-bold uppercase tracking-wider flex items-center gap-1"><User className="h-3.5 w-3.5" /> Your Name</Label>
              <Input value={personName} onChange={(e) => setPersonName(e.target.value)} placeholder="Enter your name" className="bg-gray-800 border-gray-700 text-white mt-1 h-11" disabled={!selectedSpare} data-testid="stockout-person-input" />
            </CardContent>
          </Card>

          <Button onClick={handleSpareSubmit} disabled={submitting || !selectedMachine || !selectedSpare || !quantityOut || !personName.trim()} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-black font-bold text-base mt-1 shrink-0" data-testid="stockout-submit-btn">
            {submitting ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Recording...</> : <><Minus className="h-5 w-5 mr-2" /> Record Stock Out</>}
          </Button>
        </div>
      )}

      {/* CONSUMPTION FORM */}
      {mode === "consumption" && (
        <div className="flex-1 px-4 py-3 flex flex-col gap-3 max-w-lg mx-auto w-full">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-3">
              <Label className="text-emerald-400 text-xs font-bold uppercase tracking-wider">Category</Label>
              <select value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setSelectedItem(""); }} className="w-full h-11 mt-1 px-3 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm" data-testid="consumption-category-select">
                <option value="">Select Category</option>
                {consumptionOptions.map((c) => (
                  <option key={c.category} value={c.category}>{c.category} ({c.items.length})</option>
                ))}
              </select>
            </CardContent>
          </Card>

          <Card className={`border ${selectedCategory ? 'bg-gray-900 border-gray-800' : 'bg-gray-900/50 border-gray-800/50 opacity-60'}`}>
            <CardContent className="p-3">
              <Label className="text-emerald-400 text-xs font-bold uppercase tracking-wider">Item</Label>
              <select value={selectedItem} onChange={(e) => setSelectedItem(e.target.value)} className="w-full h-11 mt-1 px-3 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm" disabled={!selectedCategory} data-testid="consumption-item-select">
                <option value="">Select Item</option>
                {selectedCategoryData?.items?.map((i) => (
                  <option key={i.id} value={i.id}>{i.item_name}{i.pantone_color ? ` (${i.pantone_color})` : ""} — Avail: {i.available_count || 0} nos</option>
                ))}
              </select>
              {selectedItemData && (
                <div className={`mt-2 px-3 py-1.5 rounded-lg text-sm font-mono font-bold ${consumptionAvailableCount > 0 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                  <Package className="h-3.5 w-3.5 inline mr-1" /> Available: {consumptionAvailableCount} nos
                  {selectedItemData.pantone_color && <span className="ml-3 text-purple-300"><Droplets className="h-3 w-3 inline mr-1" />{selectedItemData.pantone_color}</span>}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={`border ${selectedItem ? 'bg-gray-900 border-gray-800' : 'bg-gray-900/50 border-gray-800/50 opacity-60'}`}>
            <CardContent className="p-3">
              <Label className="text-emerald-400 text-xs font-bold uppercase tracking-wider">Quantity (Nos)</Label>
              <Input type="number" min="1" step="1" max={consumptionAvailableCount} value={consumptionQty} onChange={(e) => setConsumptionQty(e.target.value)} placeholder="How many?" className="bg-gray-800 border-gray-700 text-white mt-1 h-11 text-lg" disabled={!selectedItem} data-testid="consumption-qty-input" />
            </CardContent>
          </Card>

          <Card className={`border ${selectedItem ? 'bg-gray-900 border-gray-800' : 'bg-gray-900/50 border-gray-800/50 opacity-60'}`}>
            <CardContent className="p-3">
              <Label className="text-emerald-400 text-xs font-bold uppercase tracking-wider flex items-center gap-1"><User className="h-3.5 w-3.5" /> Your Name</Label>
              <Input value={consumptionPerson} onChange={(e) => setConsumptionPerson(e.target.value)} placeholder="Enter your name" className="bg-gray-800 border-gray-700 text-white mt-1 h-11" disabled={!selectedItem} data-testid="consumption-person-input" />
            </CardContent>
          </Card>

          <Card className={`border ${selectedItem ? 'bg-gray-900 border-gray-800' : 'bg-gray-900/50 border-gray-800/50 opacity-60'}`}>
            <CardContent className="p-3">
              <Label className="text-emerald-400 text-xs font-bold uppercase tracking-wider">Purpose (Optional)</Label>
              <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g., WONO-025 production" className="bg-gray-800 border-gray-700 text-white mt-1 h-11" disabled={!selectedItem} data-testid="consumption-purpose-input" />
            </CardContent>
          </Card>

          <Button onClick={handleConsumptionSubmit} disabled={submitting || !selectedItem || !consumptionQty || !consumptionPerson.trim()} className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-base mt-1 shrink-0" data-testid="consumption-submit-btn">
            {submitting ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Recording...</> : <><Minus className="h-5 w-5 mr-2" /> Record Stock Out</>}
          </Button>
        </div>
      )}
    </div>
  );
};

export default StockOut;
